// src/components/Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [adminName, setAdminName] = useState('Admin');
  const [collegeName, setCollegeName] = useState('College Admin');
  const [logoUrl, setLogoUrl] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // ── Dropdown groups ──────────────────────────────────────
  const navGroups = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'fa-chart-pie',
      single: true,
    },
    {
      label: 'Updates',
      icon: 'fa-rss',
      items: [
        { path: '/news',          icon: 'fa-newspaper',   label: 'News' },
        { path: '/events',        icon: 'fa-calendar-days', label: 'Events' },
        { path: '/notifications', icon: 'fa-bell',        label: 'Notifications' },
      ],
    },
    {
      label: 'Academic',
      icon: 'fa-graduation-cap',
      items: [
        { path: '/faculty',     icon: 'fa-user-tie',      label: 'Faculty' },
        { path: '/departments', icon: 'fa-building',      label: 'Departments' },
        { path: '/courses',     icon: 'fa-graduation-cap', label: 'Courses' },
      ],
    },
    {
      label: 'Media',
      icon: 'fa-photo-film',
      items: [
        { path: '/sliders', icon: 'fa-film',   label: 'Sliders' },
        { path: '/gallery', icon: 'fa-images', label: 'Gallery' },
      ],
    },
    {
      label: 'Reviews',
      path: '/testimonials',
      icon: 'fa-comment-dots',
      single: true,
    },
    {
      label: 'Contact',
      path: '/contact',
      icon: 'fa-address-card',
      single: true,
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: 'fa-sliders',
      single: true,
    },
  ];

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const snap = await getDocs(collection(db, 'notifications'));
        setNotificationCount(snap.size);
      } catch {
        setNotificationCount(0);
      }
    };
    const loadSettings = async () => {
      try {
        const settingsSnap = await getDoc(doc(db, 'settings', 'college_info'));
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          if (data.collegeName) setCollegeName(data.collegeName);
          if (data.logoUrl) setLogoUrl(data.logoUrl);
        }
      } catch (err) {
        console.warn('Error loading settings:', err);
      }
    };
    loadNotifications();
    loadSettings();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const isGroupActive = (group) => {
    if (group.single) return location.pathname === group.path;
    return group.items?.some((i) => i.path === location.pathname);
  };

  const toggleDropdown = (label) => {
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  return (
    <>
      <style>{`
        /* ── Reset / base ─────────────────────────────── */
        .header * { box-sizing: border-box; }

        .header {
          background: rgba(255, 255, 255, 0.48);
          backdrop-filter: blur(20px) saturate(150%);
          -webkit-backdrop-filter: blur(20px) saturate(150%);
          border-bottom: 1px solid rgba(255,255,255,0.65);
          position: sticky;
          top: 0;
          z-index: 1000;
          box-shadow: 0 4px 24px rgba(14,165,233,0.07);
        }

        .header-container {
          max-width: 1440px;
          margin: 0 auto;
          padding: 10px 28px;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        /* ── Logo ─────────────────────────────────────── */
        .header-logo a {
          text-decoration: none;
          display: flex;
          align-items: center;
        }
        .logo-img {
          height: 40px;
          width: auto;
          object-fit: contain;
          border-radius: 8px;
          padding: 3px;
          background: rgba(255,255,255,0.5);
          border: 1px solid rgba(255,255,255,0.65);
        }
        .logo-text {
          font-family: 'Space Grotesk', sans-serif;
          background: linear-gradient(135deg,#0ea5e9,#0284c7);
          color: #fff;
          padding: 7px 16px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.88rem;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          white-space: nowrap;
        }

        /* ── Nav ──────────────────────────────────────── */
        .header-nav {
          flex: 1;
          display: flex;
          justify-content: center;
        }
        .nav-list {
          display: flex;
          align-items: center;
          gap: 2px;
          list-style: none;
          margin: 0;
          padding: 4px;
          background: rgba(255,255,255,0.32);
          border: 1px solid rgba(255,255,255,0.48);
          border-radius: 12px;
        }
        .nav-item { position: relative; }

        /* single link OR dropdown trigger */
        .nav-link, .nav-trigger {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 13px;
          border-radius: 8px;
          font-size: 0.82rem;
          font-weight: 700;
          color: #334155;
          text-decoration: none;
          white-space: nowrap;
          cursor: pointer;
          border: none;
          background: none;
          font-family: inherit;
          transition: background 0.18s, color 0.18s, box-shadow 0.18s;
        }
        .nav-link:hover, .nav-trigger:hover {
          background: rgba(14,165,233,0.1);
          color: #0ea5e9;
        }
        .nav-link.active, .nav-trigger.active {
          background: #0ea5e9;
          color: #fff;
          box-shadow: 0 3px 12px rgba(14,165,233,0.28);
        }
        .nav-trigger .caret {
          font-size: 9px;
          opacity: 0.7;
          margin-left: 1px;
          transition: transform 0.2s;
        }
        .nav-trigger.open .caret { transform: rotate(180deg); }

        /* ── Dropdown panel ───────────────────────────── */
        .nav-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          left: 50%;
          transform: translateX(-50%);
          background: rgba(255,255,255,0.96);
          backdrop-filter: blur(20px) saturate(150%);
          -webkit-backdrop-filter: blur(20px) saturate(150%);
          border: 1px solid rgba(14,165,233,0.15);
          border-radius: 14px;
          box-shadow: 0 16px 48px rgba(14,165,233,0.14), 0 2px 8px rgba(0,0,0,0.06);
          padding: 6px;
          min-width: 190px;
          z-index: 2000;
          animation: ddFadeIn 0.15s ease;
        }
        @keyframes ddFadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        /* little arrow */
        .nav-dropdown::before {
          content: '';
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%) rotate(45deg);
          width: 10px; height: 10px;
          background: rgba(255,255,255,0.96);
          border-top: 1px solid rgba(14,165,233,0.15);
          border-left: 1px solid rgba(14,165,233,0.15);
          border-radius: 2px 0 0 0;
        }

        .dd-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 12px;
          border-radius: 9px;
          font-size: 0.83rem;
          font-weight: 600;
          color: #334155;
          text-decoration: none;
          transition: background 0.15s, color 0.15s;
        }
        .dd-item:hover {
          background: rgba(14,165,233,0.09);
          color: #0ea5e9;
        }
        .dd-item.active {
          background: linear-gradient(135deg, rgba(14,165,233,0.15), rgba(2,132,199,0.12));
          color: #0284c7;
        }
        .dd-icon {
          width: 28px; height: 28px;
          border-radius: 7px;
          background: rgba(14,165,233,0.1);
          display: flex; align-items: center; justify-content: center;
          font-size: 12px;
          color: #0ea5e9;
          flex-shrink: 0;
          transition: background 0.15s;
        }
        .dd-item:hover .dd-icon,
        .dd-item.active .dd-icon {
          background: rgba(14,165,233,0.18);
        }

        /* ── Actions ──────────────────────────────────── */
        .header-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }
        .notification-btn {
          position: relative;
          width: 40px; height: 40px;
          background: rgba(255,255,255,0.42);
          border: 1px solid rgba(255,255,255,0.65);
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          color: #334155;
          font-size: 1.05rem;
          transition: all 0.2s;
        }
        .notification-btn:hover {
          background: rgba(255,255,255,0.7);
          color: #0ea5e9;
          transform: translateY(-1px);
        }
        .notification-badge {
          position: absolute;
          top: -4px; right: -4px;
          background: #ef4444;
          color: #fff;
          font-size: 9px;
          font-weight: 800;
          min-width: 17px; height: 17px;
          border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          padding: 0 3px;
        }
        .profile-section {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 5px 12px 5px 5px;
          border-radius: 12px;
          background: rgba(255,255,255,0.42);
          border: 1px solid rgba(255,255,255,0.65);
          cursor: pointer;
          transition: background 0.2s;
        }
        .profile-section:hover { background: rgba(255,255,255,0.7); }
        .profile-avatar {
          width: 30px; height: 30px;
          border-radius: 50%;
          object-fit: cover;
        }
        .profile-name {
          font-size: 0.82rem;
          font-weight: 700;
          color: #0f172a;
        }
        .profile-icon { font-size: 10px; color: #64748b; }

        /* ── Mobile toggle ────────────────────────────── */
        .mobile-toggle {
          display: none;
          background: rgba(255,255,255,0.42);
          border: 1px solid rgba(255,255,255,0.65);
          border-radius: 8px;
          padding: 8px 11px;
          cursor: pointer;
          color: #334155;
          font-size: 1.1rem;
          transition: all 0.2s;
        }
        .mobile-toggle:hover { background: rgba(255,255,255,0.7); color: #0ea5e9; }

        /* ── Mobile nav ───────────────────────────────── */
        @media (max-width: 1050px) {
          .mobile-toggle { display: block; }
          .header-nav {
            display: none;
            position: absolute;
            top: 100%;
            left: 0; right: 0;
            background: rgba(255,255,255,0.98);
            backdrop-filter: blur(20px);
            padding: 12px 16px 16px;
            border-radius: 0 0 16px 16px;
            box-shadow: 0 16px 40px rgba(14,165,233,0.13);
            max-height: calc(100vh - 70px);
            overflow-y: auto;
          }
          .header-nav.open { display: block; }
          .nav-list {
            flex-direction: column;
            background: none;
            border: none;
            padding: 0;
            gap: 2px;
            align-items: stretch;
          }
          /* on mobile, dropdowns become inline expandable */
          .nav-dropdown {
            position: static;
            transform: none;
            box-shadow: none;
            border: none;
            background: rgba(14,165,233,0.04);
            border-radius: 10px;
            margin-top: 2px;
            padding: 4px;
            animation: none;
          }
          .nav-dropdown::before { display: none; }
          .nav-link, .nav-trigger {
            width: 100%;
            padding: 10px 14px;
            font-size: 0.88rem;
          }
          .profile-name, .profile-icon { display: none; }
          .profile-section { padding: 5px; }
        }
        @media (max-width: 480px) {
          .header-container { padding: 8px 12px; }
          .logo-text { font-size: 0.76rem; padding: 6px 11px; }
        }
      `}</style>

      <header className="header">
        <div className="header-container" ref={dropdownRef}>

          {/* Logo */}
          <div className="header-logo">
            <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
              {logoUrl
                ? <img src={logoUrl} alt={collegeName} className="logo-img" />
                : <span className="logo-text">{collegeName}</span>}
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button className="mobile-toggle" onClick={() => setIsMobileMenuOpen((p) => !p)}>
            <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`} />
          </button>

          {/* Nav */}
          <nav className={`header-nav ${isMobileMenuOpen ? 'open' : ''}`}>
            <ul className="nav-list">
              {navGroups.map((group) => {
                const active = isGroupActive(group);

                // ── Single link ──
                if (group.single) {
                  return (
                    <li className="nav-item" key={group.label}>
                      <Link
                        to={group.path}
                        className={`nav-link${active ? ' active' : ''}`}
                        onClick={() => { setIsMobileMenuOpen(false); setOpenDropdown(null); }}
                      >
                        <i className={`fas ${group.icon}`} />
                        {group.label}
                      </Link>
                    </li>
                  );
                }

                // ── Dropdown group ──
                const isOpen = openDropdown === group.label;
                return (
                  <li className="nav-item" key={group.label}>
                    <button
                      className={`nav-trigger${active ? ' active' : ''}${isOpen ? ' open' : ''}`}
                      onClick={() => toggleDropdown(group.label)}
                    >
                      <i className={`fas ${group.icon}`} />
                      {group.label}
                      <i className="fas fa-chevron-down caret" />
                    </button>

                    {isOpen && (
                      <div className="nav-dropdown">
                        {group.items.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`dd-item${location.pathname === item.path ? ' active' : ''}`}
                            onClick={() => { setIsMobileMenuOpen(false); setOpenDropdown(null); }}
                          >
                            <span className="dd-icon">
                              <i className={`fas ${item.icon}`} />
                            </span>
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Actions */}
          <div className="header-actions">
            <button className="notification-btn" onClick={() => navigate('/notifications')}>
              <i className="fas fa-bell" />
              {notificationCount > 0 && (
                <span className="notification-badge">{notificationCount}</span>
              )}
            </button>
            <div className="profile-section" onClick={handleLogout}>
              <img
                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"
                alt="Profile"
                className="profile-avatar"
              />
              <span className="profile-name">{adminName}</span>
              <i className="fas fa-chevron-down profile-icon" />
            </div>
          </div>

        </div>
      </header>
    </>
  );
};

export default Header;